import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Car, Plus, Pencil, Trash2, User, UserPlus, TrendingUp, Fuel, Eye, FileText, X, Upload, Calendar, Download, Camera, Wrench, CreditCard, Banknote } from 'lucide-react';
import { API, useAuth } from '../../App';

export default function VehicleManagement() {
  const { token } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicleStats, setVehicleStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showExpensesDialog, setShowExpensesDialog] = useState(false);
  const [vehicleExpenses, setVehicleExpenses] = useState(null);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [assigningVehicle, setAssigningVehicle] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({
    plate_number: '',
    model: '',
    cost_per_km: ''
  });
  const [selectedDriverId, setSelectedDriverId] = useState('_none');
  
  // Document management state
  const [showDocumentsDialog, setShowDocumentsDialog] = useState(false);
  const [selectedVehicleForDocs, setSelectedVehicleForDocs] = useState(null);
  const [vehicleDocuments, setVehicleDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showAddDocumentDialog, setShowAddDocumentDialog] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [newDocument, setNewDocument] = useState({
    document_type: '',
    expiry_date: '',
    file: null,
    preview: null
  });

  // Maintenance state
  const [maintenanceEntries, setMaintenanceEntries] = useState([]);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceFilter, setMaintenanceFilter] = useState({ month: '', year: '' });
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  const [maintenanceForm, setMaintenanceForm] = useState({
    vehicle_id: '', date: '', kilometers: '', operation_type: '', price: '', notes: '', payment_method: 'card'
  });
  const [maintenanceTotalCost, setMaintenanceTotalCost] = useState(0);

  const fetchVehicleExpenses = async (vehicleId) => {
    setLoadingExpenses(true);
    try {
      
      const response = await fetch(
        `${API}/vehicles/${vehicleId}/all-expenses?month=${selectedMonth}&year=${selectedYear}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setVehicleExpenses(data);
        setShowExpensesDialog(true);
      } else {
        toast.error('Failed to load vehicle expenses');
      }
    } catch (error) {
      console.error('Error fetching vehicle expenses:', error);
      toast.error('Failed to load vehicle expenses');
    } finally {
      setLoadingExpenses(false);
    }
  };

  const fetchMaintenance = async () => {
    setMaintenanceLoading(true);
    try {
      let url = `${API}/vehicles/maintenance`;
      const params = [];
      if (maintenanceFilter.month && maintenanceFilter.year) {
        params.push(`month=${maintenanceFilter.month}`, `year=${maintenanceFilter.year}`);
      }
      if (params.length) url += '?' + params.join('&');
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setMaintenanceEntries(data.entries || []);
        setMaintenanceTotalCost(data.total_cost || 0);
      }
    } catch (err) {
      console.error('Error fetching maintenance:', err);
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const handleCreateMaintenance = async () => {
    if (!maintenanceForm.vehicle_id || !maintenanceForm.date || !maintenanceForm.operation_type || !maintenanceForm.price) {
      toast.error('Fill in all required fields');
      return;
    }
    try {
      const body = {
        vehicle_id: maintenanceForm.vehicle_id,
        date: new Date(maintenanceForm.date).toISOString(),
        kilometers: maintenanceForm.kilometers ? parseFloat(maintenanceForm.kilometers) : null,
        operation_type: maintenanceForm.operation_type,
        price: parseFloat(maintenanceForm.price),
        notes: maintenanceForm.notes || null,
        payment_method: maintenanceForm.payment_method
      };
      const res = await fetch(`${API}/vehicles/maintenance`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success('Maintenance entry added');
        setShowMaintenanceDialog(false);
        setMaintenanceForm({ vehicle_id: '', date: '', kilometers: '', operation_type: '', price: '', notes: '', payment_method: 'card' });
        fetchMaintenance();
        fetchVehicleStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error');
      }
    } catch (err) {
      toast.error('Error creating entry');
    }
  };

  const handleDeleteMaintenance = async (entryId) => {
    if (!window.confirm('Delete this maintenance entry? The linked financial entry will also be deleted.')) return;
    try {
      const res = await fetch(`${API}/vehicles/maintenance/${entryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Entry deleted');
        fetchMaintenance();
        fetchVehicleStats();
      } else {
        toast.error('Error deleting entry');
      }
    } catch (err) {
      toast.error('Error deleting entry');
    }
  };

  const fetchVehicles = async () => {
    try {
      
      const response = await fetch(`${API}/vehicles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVehicles(data);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Failed to fetch vehicles');
    }
  };

  const fetchDrivers = async () => {
    try {
      
      const response = await fetch(`${API}/drivers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDrivers(data);
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchVehicleStats = async () => {
    try {
      
      const response = await fetch(`${API}/vehicles/stats/summary?month=${selectedMonth}&year=${selectedYear}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVehicleStats(data);
      }
    } catch (error) {
      console.error('Error fetching vehicle stats:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchVehicles(), fetchDrivers()]);
      setLoading(false);
    };
    loadData();
    fetchMaintenance();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchVehicleStats();
    }
  }, [selectedMonth, selectedYear, loading]);

  useEffect(() => {
    fetchMaintenance();
  }, [maintenanceFilter.month, maintenanceFilter.year]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    
    const url = editingVehicle 
      ? `${API}/vehicles/${editingVehicle.id}`
      : `${API}/vehicles`;
    
    const method = editingVehicle ? 'PUT' : 'POST';
    
    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plate_number: formData.plate_number,
          model: formData.model || null,
          cost_per_km: formData.cost_per_km ? parseFloat(formData.cost_per_km) : null
        })
      });
      
      if (response.ok) {
        toast.success(editingVehicle ? 'Vehicle updated' : 'Vehicle created');
        setShowDialog(false);
        setEditingVehicle(null);
        setFormData({ plate_number: '', model: '', cost_per_km: '' });
        fetchVehicles();
        fetchVehicleStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save vehicle');
      }
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error('Failed to save vehicle');
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      plate_number: vehicle.plate_number || '',
      model: vehicle.model || '',
      cost_per_km: vehicle.cost_per_km?.toString() || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (vehicle) => {
    if (!window.confirm(`Delete vehicle ${vehicle.plate_number}?`)) return;
    
    
    try {
      const response = await fetch(`${API}/vehicles/${vehicle.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Vehicle deleted');
        fetchVehicles();
        fetchVehicleStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete vehicle');
      }
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error('Failed to delete vehicle');
    }
  };

  const handleAssign = (vehicle) => {
    setAssigningVehicle(vehicle);
    setSelectedDriverId(vehicle.assigned_driver_id || '_none');
    setShowAssignDialog(true);
  };

  const submitAssignment = async () => {
    
    const driverId = selectedDriverId === '_none' ? '' : selectedDriverId;
    
    if (!driverId) {
      toast.error('Please select a driver to add');
      return;
    }
    
    try {
      const response = await fetch(`${API}/vehicles/${assigningVehicle.id}/assign-driver?driver_id=${driverId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchVehicles();
        // Refresh the assigning vehicle to show updated driver list
        const res = await fetch(`${API}/vehicles/${assigningVehicle.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          setAssigningVehicle(await res.json());
        }
        setSelectedDriverId('_none');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign vehicle');
      }
    } catch (error) {
      console.error('Error assigning vehicle:', error);
      toast.error('Failed to assign vehicle');
    }
  };

  const getVehicleStat = (vehicleId) => {
    if (!vehicleStats?.vehicles) return null;
    return vehicleStats.vehicles.find(v => v.vehicle_id === vehicleId);
  };

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 3; y--) {
    years.push(y);
  }

  // Document management functions
  const openDocumentsDialog = async (vehicle) => {
    setSelectedVehicleForDocs(vehicle);
    setShowDocumentsDialog(true);
    await fetchVehicleDocuments(vehicle.id);
  };

  const fetchVehicleDocuments = async (vehicleId) => {
    setLoadingDocuments(true);
    try {
      
      const response = await fetch(`${API}/vehicles/${vehicleId}/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVehicleDocuments(data);
      } else {
        toast.error('Failed to load documents');
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDocumentFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setNewDocument(prev => ({ ...prev, file, preview: e.target.result }));
        };
        reader.readAsDataURL(file);
      } else {
        setNewDocument(prev => ({ ...prev, file, preview: null }));
      }
    }
  };

  const handleCapturePhoto = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      
      // Create video element to capture frame
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();
      
      // Wait for video to be ready
      await new Promise(resolve => {
        video.onloadedmetadata = resolve;
      });
      
      // Create canvas and capture frame
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      // Stop the stream
      stream.getTracks().forEach(track => track.stop());
      
      // Convert to blob
      canvas.toBlob((blob) => {
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        const preview = canvas.toDataURL('image/jpeg');
        setNewDocument(prev => ({ ...prev, file, preview }));
      }, 'image/jpeg', 0.8);
      
    } catch (error) {
      console.error('Error capturing photo:', error);
      toast.error('Failed to access camera');
    }
  };

  const uploadDocument = async () => {
    if (!newDocument.document_type.trim()) {
      toast.error('Please enter document type');
      return;
    }
    if (!newDocument.file) {
      toast.error('Please select a file');
      return;
    }

    setUploadingDocument(true);
    try {
      
      const formData = new FormData();
      formData.append('file', newDocument.file);
      formData.append('document_type', newDocument.document_type);
      if (newDocument.expiry_date) {
        formData.append('expiry_date', newDocument.expiry_date);
      }

      const response = await fetch(`${API}/vehicles/${selectedVehicleForDocs.id}/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        toast.success('Document uploaded successfully');
        setShowAddDocumentDialog(false);
        setNewDocument({ document_type: '', expiry_date: '', file: null, preview: null });
        await fetchVehicleDocuments(selectedVehicleForDocs.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload document');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm('Delete this document?')) return;

    try {
      
      const response = await fetch(`${API}/vehicles/${selectedVehicleForDocs.id}/documents/${documentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Document deleted');
        await fetchVehicleDocuments(selectedVehicleForDocs.id);
      } else {
        toast.error('Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const downloadDocument = (doc) => {
    if (doc.file_url) {
      window.open(doc.file_url, '_blank');
    }
  };

  const isDocumentExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const isDocumentExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vehicle Management</h1>
          <p className="text-gray-500">Manage your fleet vehicles and track their performance</p>
        </div>
        <Button onClick={() => { setEditingVehicle(null); setFormData({ plate_number: '', model: '', cost_per_km: '' }); setShowDialog(true); }} data-testid="add-vehicle-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Vehicle
        </Button>
      </div>

      {/* Monthly Stats Summary */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Monthly Vehicle Statistics
              </CardTitle>
              <CardDescription>Track kilometers and costs per vehicle</CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="w-32" data-testid="stats-month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-24" data-testid="stats-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {vehicleStats?.vehicles?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vehicleStats.vehicles.map(stat => (
                <div key={stat.vehicle_id} className="p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-lg">{stat.plate_number}</span>
                    {stat.model && <span className="text-sm text-gray-500">{stat.model}</span>}
                  </div>
                  {stat.assigned_driver && (
                    <div className="text-sm text-gray-600 mb-2 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {stat.assigned_driver}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-white p-2 rounded">
                      <div className="text-gray-500">KM Tracked</div>
                      <div className="font-semibold text-blue-600">{stat.total_km?.toLocaleString() || 0} km</div>
                      {stat.first_km > 0 && (
                        <div className="text-xs text-gray-400">
                          {stat.first_km?.toLocaleString()} → {stat.latest_km?.toLocaleString()}
                        </div>
                      )}
                    </div>
                    <div className="bg-white p-2 rounded">
                      <div className="text-gray-500">Petrol Cost</div>
                      <div className="font-semibold text-amber-600">€{stat.total_petrol_cost?.toFixed(2) || '0.00'}</div>
                    </div>
                    <div className="bg-white p-2 rounded">
                      <div className="text-gray-500">Other Expenses</div>
                      <div className="font-semibold text-red-600">€{stat.total_expense_cost?.toFixed(2) || '0.00'}</div>
                    </div>
                    <div className="bg-white p-2 rounded">
                      <div className="text-gray-500">Maintenance</div>
                      <div className="font-semibold text-orange-600">€{stat.total_maintenance_cost?.toFixed(2) || '0.00'}</div>
                      {stat.maintenance_entries > 0 && <div className="text-xs text-gray-400">{stat.maintenance_entries} voci</div>}
                    </div>
                    <div className="bg-white p-2 rounded">
                      <div className="text-gray-500">Total Cost</div>
                      <div className="font-semibold text-purple-600">€{stat.total_cost?.toFixed(2) || '0.00'}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    {stat.rides_completed} rides completed • {stat.petrol_entries} petrol entries
                  </div>
                    <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-3"
                    onClick={() => fetchVehicleExpenses(stat.vehicle_id)}
                    disabled={loadingExpenses}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View All Costs
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No vehicle statistics available for this period</p>
          )}
        </CardContent>
      </Card>

      {/* Vehicles Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Car className="h-5 w-5" />
            Fleet Vehicles
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plate Number</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Cost/km</TableHead>
                <TableHead>Assigned Driver</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vehicles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No vehicles found. Add your first vehicle to get started.
                  </TableCell>
                </TableRow>
              ) : (
                vehicles.map(vehicle => {
                  const stat = getVehicleStat(vehicle.id);
                  return (
                    <TableRow key={vehicle.id} data-testid={`vehicle-row-${vehicle.id}`}>
                      <TableCell className="font-medium">{vehicle.plate_number}</TableCell>
                      <TableCell>{vehicle.model || '-'}</TableCell>
                      <TableCell>{vehicle.cost_per_km ? `€${vehicle.cost_per_km.toFixed(2)}` : '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          {vehicle.assigned_drivers && vehicle.assigned_drivers.length > 0 ? (
                            vehicle.assigned_drivers.map((driver, idx) => (
                              <Badge key={driver.id} variant="outline" className="bg-green-50">
                                <User className="h-3 w-3 mr-1" />
                                {driver.name}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-gray-400">Unassigned</span>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => handleAssign(vehicle)} data-testid={`assign-driver-btn-${vehicle.id}`}>
                            <UserPlus className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={vehicle.is_active ? 'default' : 'secondary'}>
                          {vehicle.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => openDocumentsDialog(vehicle)} data-testid={`docs-vehicle-btn-${vehicle.id}`} title="Documents">
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(vehicle)} data-testid={`edit-vehicle-btn-${vehicle.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(vehicle)} data-testid={`delete-vehicle-btn-${vehicle.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Maintenance Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                Maintenance
              </CardTitle>
              <CardDescription>Vehicle maintenance log</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={maintenanceFilter.month || '_all'} onValueChange={(v) => setMaintenanceFilter(prev => ({ ...prev, month: v === '_all' ? '' : v, year: v === '_all' ? '' : (prev.year || new Date().getFullYear().toString()) }))}>
                <SelectTrigger className="w-36" data-testid="maintenance-month-filter">
                  <SelectValue placeholder="All months" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All months</SelectItem>
                  {months.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {maintenanceFilter.month && (
                <Select value={maintenanceFilter.year || new Date().getFullYear().toString()} onValueChange={(v) => setMaintenanceFilter(prev => ({ ...prev, year: v }))}>
                  <SelectTrigger className="w-24" data-testid="maintenance-year-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button onClick={() => { setMaintenanceForm({ vehicle_id: '', date: '', kilometers: '', operation_type: '', price: '', notes: '', payment_method: 'card' }); setShowMaintenanceDialog(true); }} data-testid="add-maintenance-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {maintenanceLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
            </div>
          ) : maintenanceEntries.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No maintenance entries{maintenanceFilter.month ? ' for this period' : ''}</p>
          ) : (
            <>
              <div className="mb-3 flex items-center gap-4">
                <Badge variant="outline" className="text-orange-700 border-orange-300 bg-orange-50 px-3 py-1">
                  {maintenanceEntries.length} entries • Total: €{maintenanceTotalCost.toFixed(2)}
                </Badge>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Plate</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>KM</TableHead>
                      <TableHead>Operation Type</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {maintenanceEntries.map(entry => (
                      <TableRow key={entry.id} data-testid={`maintenance-row-${entry.id}`}>
                        <TableCell className="font-medium">{entry.vehicle_plate}</TableCell>
                        <TableCell>{entry.date ? new Date(entry.date).toLocaleDateString('it-IT') : '-'}</TableCell>
                        <TableCell>{entry.kilometers ? entry.kilometers.toLocaleString() : '-'}</TableCell>
                        <TableCell>{entry.operation_type}</TableCell>
                        <TableCell className="font-semibold text-orange-700">€{entry.price?.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={entry.payment_method === 'cash' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'}>
                            {entry.payment_method === 'cash' ? <><Banknote className="h-3 w-3 mr-1" />Cash</> : <><CreditCard className="h-3 w-3 mr-1" />Card</>}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-gray-500 text-sm">{entry.notes || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteMaintenance(entry.id)} data-testid={`delete-maintenance-${entry.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Maintenance Dialog */}
      <Dialog open={showMaintenanceDialog} onOpenChange={setShowMaintenanceDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              New Maintenance Entry
            </DialogTitle>
            <DialogDescription>Add a vehicle maintenance expense</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Vehicle *</Label>
              <Select value={maintenanceForm.vehicle_id || '_none'} onValueChange={(v) => setMaintenanceForm(prev => ({ ...prev, vehicle_id: v === '_none' ? '' : v }))}>
                <SelectTrigger data-testid="maintenance-vehicle-select">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-- Select --</SelectItem>
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.plate_number} {v.model ? `(${v.model})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={maintenanceForm.date} onChange={(e) => setMaintenanceForm(prev => ({ ...prev, date: e.target.value }))} data-testid="maintenance-date-input" />
              </div>
              <div>
                <Label>KM (optional)</Label>
                <Input type="number" placeholder="es. 45000" value={maintenanceForm.kilometers} onChange={(e) => setMaintenanceForm(prev => ({ ...prev, kilometers: e.target.value }))} data-testid="maintenance-km-input" />
              </div>
            </div>
            <div>
              <Label>Operation Type *</Label>
              <Input placeholder="e.g. Oil change, Brakes, Service..." value={maintenanceForm.operation_type} onChange={(e) => setMaintenanceForm(prev => ({ ...prev, operation_type: e.target.value }))} data-testid="maintenance-operation-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price (€) *</Label>
                <Input type="number" step="0.01" min="0" placeholder="es. 120.50" value={maintenanceForm.price} onChange={(e) => setMaintenanceForm(prev => ({ ...prev, price: e.target.value }))} data-testid="maintenance-price-input" />
              </div>
              <div>
                <Label>Payment Method</Label>
                <div className="flex gap-2 mt-1">
                  <Button type="button" variant={maintenanceForm.payment_method === 'card' ? 'default' : 'outline'} className="flex-1" onClick={() => setMaintenanceForm(prev => ({ ...prev, payment_method: 'card' }))} data-testid="maintenance-pay-card">
                    <CreditCard className="h-4 w-4 mr-1" /> Card
                  </Button>
                  <Button type="button" variant={maintenanceForm.payment_method === 'cash' ? 'default' : 'outline'} className="flex-1" onClick={() => setMaintenanceForm(prev => ({ ...prev, payment_method: 'cash' }))} data-testid="maintenance-pay-cash">
                    <Banknote className="h-4 w-4 mr-1" /> Cash
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {maintenanceForm.payment_method === 'card' ? 'Will be recorded as Card Outcome' : 'Will be recorded as Cash Outcome'}
                </p>
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input placeholder="Additional details..." value={maintenanceForm.notes} onChange={(e) => setMaintenanceForm(prev => ({ ...prev, notes: e.target.value }))} data-testid="maintenance-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMaintenanceDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateMaintenance} data-testid="save-maintenance-btn">
              <Plus className="h-4 w-4 mr-2" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Vehicle Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}</DialogTitle>
            <DialogDescription>
              {editingVehicle ? 'Update the vehicle details' : 'Enter the vehicle information'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="plate_number">Plate Number *</Label>
              <Input
                id="plate_number"
                value={formData.plate_number}
                onChange={(e) => setFormData({ ...formData, plate_number: e.target.value })}
                placeholder="e.g., ABC-1234"
                required
                data-testid="vehicle-plate-input"
              />
            </div>
            <div>
              <Label htmlFor="model">Model (Optional)</Label>
              <Input
                id="model"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                placeholder="e.g., Toyota Camry"
                data-testid="vehicle-model-input"
              />
            </div>
            <div>
              <Label htmlFor="cost_per_km">Cost per KM (€)</Label>
              <Input
                id="cost_per_km"
                type="number"
                step="0.01"
                min="0"
                value={formData.cost_per_km}
                onChange={(e) => setFormData({ ...formData, cost_per_km: e.target.value })}
                placeholder="e.g., 0.15"
                data-testid="vehicle-cost-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-vehicle-btn">
                {editingVehicle ? 'Update Vehicle' : 'Create Vehicle'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Driver Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Drivers for Vehicle</DialogTitle>
            <DialogDescription>
              {assigningVehicle && `Assign drivers to ${assigningVehicle.plate_number} ${assigningVehicle.model ? `(${assigningVehicle.model})` : ''}`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Currently Assigned Drivers */}
            {assigningVehicle?.assigned_drivers && assigningVehicle.assigned_drivers.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-2 block">Currently Assigned Drivers</Label>
                <div className="space-y-2">
                  {assigningVehicle.assigned_drivers.map(driver => (
                    <div key={driver.id} className="flex items-center justify-between p-2 bg-green-50 rounded-sm border border-green-200">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium">{driver.name}</span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-600 hover:text-red-700 h-7 px-2"
                        onClick={async () => {
                          
                          try {
                            await fetch(`${API}/vehicles/${assigningVehicle.id}/unassign-driver?driver_id=${driver.id}`, {
                              method: 'PUT',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            toast.success(`${driver.name} removed from vehicle`);
                            fetchVehicles();
                            // Refresh the assigning vehicle data
                            const res = await fetch(`${API}/vehicles/${assigningVehicle.id}`, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (res.ok) {
                              setAssigningVehicle(await res.json());
                            }
                          } catch (err) {
                            toast.error('Failed to remove driver');
                          }
                        }}
                        data-testid={`remove-driver-${driver.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Add New Driver */}
            <div>
              <Label htmlFor="driver" className="text-sm font-medium mb-2 block">Add Driver</Label>
              <Select value={selectedDriverId} onValueChange={setSelectedDriverId}>
                <SelectTrigger data-testid="assign-driver-select">
                  <SelectValue placeholder="Select a driver to add" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-- Select Driver --</SelectItem>
                  {drivers
                    .filter(d => !assigningVehicle?.assigned_drivers?.some(ad => ad.id === d.id))
                    .map(driver => (
                      <SelectItem key={driver.id} value={driver.id}>
                        {driver.name} {driver.vehicle_id && driver.vehicle_id !== assigningVehicle?.id && `(assigned to another vehicle)`}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Close</Button>
            <Button 
              onClick={submitAssignment} 
              disabled={!selectedDriverId || selectedDriverId === '_none'}
              data-testid="confirm-assign-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add Driver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vehicle All Expenses Dialog */}
      <Dialog open={showExpensesDialog} onOpenChange={setShowExpensesDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="h-5 w-5" />
              Vehicle Costs - {vehicleExpenses?.vehicle?.plate_number}
              {vehicleExpenses?.vehicle?.model && ` (${vehicleExpenses.vehicle.model})`}
            </DialogTitle>
            <DialogDescription>
              All petrol and expenses for {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
            </DialogDescription>
          </DialogHeader>
          
          {vehicleExpenses && (
            <div className="space-y-4">
              {/* KM Tracking Summary */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">Odometer Tracking</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">First Reading:</span>
                    <span className="font-semibold ml-2">{vehicleExpenses.km_tracking.first_km?.toLocaleString() || 0} km</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Latest Reading:</span>
                    <span className="font-semibold ml-2">{vehicleExpenses.km_tracking.latest_km?.toLocaleString() || 0} km</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Total Tracked:</span>
                    <span className="font-bold text-blue-600 ml-2">{vehicleExpenses.km_tracking.total_km_tracked?.toLocaleString() || 0} km</span>
                  </div>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <div className="text-sm text-gray-500">Petrol ({vehicleExpenses.summary.petrol_entries_count})</div>
                  <div className="text-xl font-bold text-amber-600">€{vehicleExpenses.summary.total_petrol_cost}</div>
                </div>
                <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                  <div className="text-sm text-gray-500">Expenses ({vehicleExpenses.summary.expenses_count})</div>
                  <div className="text-xl font-bold text-red-600">€{vehicleExpenses.summary.total_expense_cost}</div>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <div className="text-sm text-gray-500">Total Cost</div>
                  <div className="text-xl font-bold text-purple-600">€{vehicleExpenses.summary.total_cost}</div>
                </div>
              </div>

              {/* Cost Details */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Cost Details ({vehicleExpenses.actions.length})</h4>
                {vehicleExpenses.actions.length > 0 ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {vehicleExpenses.actions.map((action, idx) => (
                      <div 
                        key={`${action.type}-${action.id}-${idx}`}
                        className={`p-3 rounded-lg border ${
                          action.type === 'petrol' ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {action.type === 'petrol' && <Fuel className="h-4 w-4 text-amber-600" />}
                            {action.type === 'expense' && <FileText className="h-4 w-4 text-red-600" />}
                            <Badge variant="outline" className="text-xs">
                              {action.type.charAt(0).toUpperCase() + action.type.slice(1)}
                            </Badge>
                            <span className="text-sm font-medium">{action.description}</span>
                          </div>
                          <span className="font-semibold text-gray-700">
                            -€{action.amount?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                          <span>{action.date ? new Date(action.date).toLocaleDateString() : 'N/A'}</span>
                          {action.driver_name && <span>Driver: {action.driver_name}</span>}
                          {action.kilometers && <span>Odometer: {action.kilometers?.toLocaleString()} km</span>}
                          {action.category && <span>Category: {action.category}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No cost entries recorded for this period</p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExpensesDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog open={showDocumentsDialog} onOpenChange={setShowDocumentsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Vehicle Documents - {selectedVehicleForDocs?.plate_number}
            </DialogTitle>
            <DialogDescription>
              Manage documents for this vehicle (Insurance, Registration, Inspection, etc.)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Add Document Button */}
            <div className="flex justify-end">
              <Button onClick={() => setShowAddDocumentDialog(true)} data-testid="add-document-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Document
              </Button>
            </div>

            {/* Documents List */}
            {loadingDocuments ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : vehicleDocuments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No documents uploaded yet</p>
                <p className="text-sm">Click "Add Document" to upload the first document</p>
              </div>
            ) : (
              <div className="space-y-3">
                {vehicleDocuments.map(doc => (
                  <div 
                    key={doc.id} 
                    className={`p-4 rounded-lg border ${
                      isDocumentExpired(doc.expiry_date) 
                        ? 'bg-red-50 border-red-200' 
                        : isDocumentExpiringSoon(doc.expiry_date) 
                          ? 'bg-amber-50 border-amber-200' 
                          : 'bg-white border-gray-200'
                    }`}
                    data-testid={`document-item-${doc.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">{doc.document_type}</span>
                          {isDocumentExpired(doc.expiry_date) && (
                            <Badge variant="destructive" className="text-xs">Expired</Badge>
                          )}
                          {isDocumentExpiringSoon(doc.expiry_date) && !isDocumentExpired(doc.expiry_date) && (
                            <Badge className="bg-amber-500 text-xs">Expiring Soon</Badge>
                          )}
                        </div>
                        {doc.expiry_date && (
                          <div className="flex items-center gap-1 text-sm text-gray-500 mb-1">
                            <Calendar className="h-3 w-3" />
                            Expires: {new Date(doc.expiry_date).toLocaleDateString()}
                          </div>
                        )}
                        <div className="text-xs text-gray-400">
                          Uploaded: {new Date(doc.created_at).toLocaleDateString()}
                          {doc.file_name && ` • ${doc.file_name}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {doc.file_url && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => downloadDocument(doc)}
                            data-testid={`download-doc-${doc.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteDocument(doc.id)}
                          data-testid={`delete-doc-${doc.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {/* Preview for images */}
                    {doc.file_url && doc.file_type?.startsWith('image/') && (
                      <div className="mt-3">
                        <img 
                          src={doc.file_url} 
                          alt={doc.document_type}
                          className="max-h-32 rounded border cursor-pointer hover:opacity-80"
                          onClick={() => window.open(doc.file_url, '_blank')}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocumentsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Document Dialog */}
      <Dialog open={showAddDocumentDialog} onOpenChange={setShowAddDocumentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Document</DialogTitle>
            <DialogDescription>
              Upload a document for {selectedVehicleForDocs?.plate_number}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Document Type */}
            <div className="space-y-2">
              <Label htmlFor="document_type">Document Type *</Label>
              <Input
                id="document_type"
                placeholder="e.g., Insurance, Registration, MOT, etc."
                value={newDocument.document_type}
                onChange={(e) => setNewDocument(prev => ({ ...prev, document_type: e.target.value }))}
                data-testid="document-type-input"
              />
            </div>

            {/* Expiry Date */}
            <div className="space-y-2">
              <Label htmlFor="expiry_date">Expiry Date (Optional)</Label>
              <Input
                id="expiry_date"
                type="date"
                value={newDocument.expiry_date}
                onChange={(e) => setNewDocument(prev => ({ ...prev, expiry_date: e.target.value }))}
                data-testid="document-expiry-input"
              />
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>Upload File *</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => document.getElementById('doc-file-input').click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Choose File
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCapturePhoto}
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Take Photo
                </Button>
              </div>
              <input
                id="doc-file-input"
                type="file"
                accept="image/*,.pdf,.doc,.docx"
                className="hidden"
                onChange={handleDocumentFileSelect}
              />
              
              {/* File Preview */}
              {newDocument.file && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                  {newDocument.preview ? (
                    <div className="relative">
                      <img 
                        src={newDocument.preview} 
                        alt="Preview" 
                        className="max-h-40 rounded mx-auto"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="absolute top-0 right-0 text-red-600"
                        onClick={() => setNewDocument(prev => ({ ...prev, file: null, preview: null }))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-blue-500" />
                        <span className="text-sm">{newDocument.file.name}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600"
                        onClick={() => setNewDocument(prev => ({ ...prev, file: null, preview: null }))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowAddDocumentDialog(false);
                setNewDocument({ document_type: '', expiry_date: '', file: null, preview: null });
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={uploadDocument}
              disabled={uploadingDocument || !newDocument.document_type || !newDocument.file}
              data-testid="upload-document-btn"
            >
              {uploadingDocument ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
